import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ArrowRight from '@ui/svgs/ArrowRight';
import { getFromLocalStorage, setToLocalStorage } from '../../../../lib/localStorage';
import { AlertErrorIconSvg, AlertInfoIconSvg, AlertSuccessIconSvg, AlertWarningIconSvg } from '../../../svgs/AlertIcons';
import { colors } from '../../../themeUtils/colors';
import { typography } from '../../../themeUtils/typography';
import ButtonUI from '../Button/ButtonUI';
import { CloseIcon } from '@ui/svgs/CloseIcon';

const variantConfig = {
  informational: {
    iconComponent: AlertInfoIconSvg,
    background: colors.bg.info,
    border: colors.border.info,
  },
  warning: {
    iconComponent: AlertWarningIconSvg,
    background: colors.bg.warning,
    border: colors.border.warning,
  },
  error: {
    iconComponent: AlertErrorIconSvg,
    background: colors.bg.error,
    border: colors.border.error,
  },
  success: {
    iconComponent: AlertSuccessIconSvg,
    background: colors.bg.success,
    border: colors.border.success,
  },
};

const variantAliases = {
  info: 'informational',
  fail: 'error',
};

const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  border: 1px solid ${(p) => p.$variant.border};
  border-left-width: ${({ theme }) => theme.unit(1)};
  border-radius: 8px;
  background: ${(p) => p.$variant.background};
  padding: ${({ theme }) => theme.unit(1.5)};
  color: ${colors.text.primary};
`;

const IconWrapper = styled.div`
  height: ${({ theme }) => theme.unit(3)};
  width: ${({ theme }) => theme.unit(3)};
  flex-shrink: 0;
  margin-right: ${({ theme }) => theme.unit(0.5)};

  svg {
    height: ${({ theme }) => theme.unit(3)};
    width: ${({ theme }) => theme.unit(3)};
    display: block;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  flex: 1;
  gap: ${({ theme }) => theme.unit(0.5)};
`;

const Heading = styled.p`
  ${typography.body.md}
  font-weight: var(--typography-font-weight-bold);
  color: ${colors.text.primary};
`;

const Message = styled.p`
  ${typography.body.md}
  color: ${colors.text.primary};
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
  height: ${({ theme }) => theme.unit(3)};
  width: ${({ theme }) => theme.unit(3)};

  color: ${({ theme }) => theme.c.icon.interactive};
`;

const resolveVariant = (type) => {
  const resolvedType = variantAliases[type] || type;
  return variantConfig[resolvedType] || variantConfig.informational;
};

const Notification = ({
  id,
  type = 'warning',
  headerText,
  message,
  mobileHeaderText,
  mobileMessage,
  linkUrl,
  linkText,
  displayFrequency,
  closeIconLabel,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const notificationExpiry = getFromLocalStorage(`notification-${id}`) || 0;

    if (notificationExpiry < Date.now()) {
      setShow(true);
    }
  }, [id]);

  const variant = resolveVariant(type);
  const IconComponent = variant.iconComponent;

  const onCloseHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    /* set session cookie with id of element */
    if (displayFrequency !== null) {
      let expiryDate = 0;
      if (typeof displayFrequency === 'number' && displayFrequency > 0) {
        const day = 1000 * 60 * 60 * 24;
        expiryDate = Date.now() + day * displayFrequency;
      }

      setToLocalStorage(`notification-${id}`, expiryDate);
    }
    setShow(false);
  };

  const resolvedHeaderText = headerText || mobileHeaderText;
  const resolvedMessage = message || mobileMessage;

  if (!show) {
    return null;
  }

  return (
    <Alert role="alert" aria-live="polite" aria-atomic="true" $variant={variant} data-track-section="global-alert-closure">
      <IconWrapper aria-hidden="true">
        <IconComponent />
      </IconWrapper>
      <Content>
        {resolvedHeaderText && <Heading>{resolvedHeaderText}</Heading>}
        <Message>
          {resolvedMessage}
        </Message>
        {linkUrl && linkText && (
          <ButtonUI
            variant="tertiary"
            size="small"
            href={linkUrl}
            buttonText={linkText}
            renderRightIcon={({ className }) => <ArrowRight className={className} />}
          />
        )}
      </Content>
      <CloseButton onClick={onCloseHandler} type="button" data-testid="close-alert" aria-label={closeIconLabel}>
        <CloseIcon />
      </CloseButton>
    </Alert>
  );
};

export default Notification;
